import i18nFactory from '@yandex-int/i18n';

import { UrlHelpers } from '../../service/UrlHelpers';
import { APP_PREFIX, LinkTarget } from '../../typings/common';
import {
    SecondLevelSidebarMenuIds,
    SidebarDynamicItemsIds,
    SidebarLinkData,
    SidebarStaticItemsIds
} from '../../typings/sidebar';

import * as keyset from './sidebar.i18n';

const i18n = i18nFactory(keyset);

export const getSidebarSubmenuMap = (): Partial<
    Record<SidebarStaticItemsIds | SidebarDynamicItemsIds, SecondLevelSidebarMenuIds>
> => {
    return {
        [SidebarStaticItemsIds.addEntity]: SecondLevelSidebarMenuIds.addEntity,
        [SidebarStaticItemsIds.openLibraryMenu]: SecondLevelSidebarMenuIds.libraryMenu,
        [SidebarStaticItemsIds.openReportsMenu]: SecondLevelSidebarMenuIds.reportsMenu,
        [SidebarStaticItemsIds.openInfoMenu]: SecondLevelSidebarMenuIds.infoMenu
    };
};

export const sidebarTitles: Record<SidebarStaticItemsIds | SidebarDynamicItemsIds, string> = {
    [SidebarStaticItemsIds.addEntity]: i18n('add-entity'),
    [SidebarStaticItemsIds.goToRecommendations]: i18n('recommendations'),
    [SidebarStaticItemsIds.goToCampaigns]: i18n('campaigns'),
    [SidebarStaticItemsIds.goToShowClients]: i18n('all-clients'),
    [SidebarStaticItemsIds.openReportsMenu]: i18n('reports'),
    [SidebarStaticItemsIds.openLibraryMenu]: i18n('library'),
    [SidebarStaticItemsIds.openInfoMenu]: i18n('info'),
    [SidebarStaticItemsIds.tools]: i18n('tools'),
    [SidebarDynamicItemsIds.goToEdit]: i18n('edit')
};

const sidebarLinks = {
    'statistic-by-all-campaigns': {
        text: i18n('statistic-by-all-campaigns'),
        target: LinkTarget.blank
    },
    'order-reports': {
        text: i18n('order-reports'),
        target: LinkTarget.blank
    },
    'phrases-positions-monitoring': {
        text: i18n('phrases-positions-monitoring'),
        target: LinkTarget.blank
    },
    'retargeting-and-audiences': {
        text: i18n('retargeting-and-audiences'),
        target: LinkTarget.blank
    },
    feeds: {
        text: i18n('feeds'),
        target: LinkTarget.blank
    },
    creatives: {
        text: i18n('creatives'),
        target: LinkTarget.blank
    },
    'turbo-pages': {
        text: i18n('turbo-pages'),
        target: LinkTarget.blank
    },
    'mobile-apps': {
        text: i18n('mobile-apps')
    },
    'agency-clients': {
        text: i18n('agency-clients'),
        target: LinkTarget.blank
    },
    'client-representatives': {
        text: i18n('client-representatives'),
        target: LinkTarget.blank
    },
    'internationalization-interface': {
        text: i18n('internationalization-interface'),
        target: LinkTarget.blank
    },
    'upload-documents': {
        text: i18n('upload-documents'),
        target: LinkTarget.blank
    },
    'statistic-by-all-old-campaigns': {
        text: i18n('statistic-by-all-old-campaigns'),
        target: LinkTarget.blank
    },
    'minus-keywords-library': {
        text: i18n('minus-keywords-library')
    },
    'price-packages': {
        text: i18n('price-packages-grid')
    }
};

export const getSidebarLinks = (): typeof sidebarLinks => {
    return sidebarLinks;
};

export type SidebarLinkKey = keyof typeof sidebarLinks;
export type SidebarLinksActions = Record<SidebarLinkKey, string>;

export const getSidebarLinksHrefs = ({ login, ClientID, agencyLogin }: SidebarLinkData): SidebarLinksActions => {
    const sidebarActions: SidebarLinksActions = {
        'statistic-by-all-campaigns': UrlHelpers.getDirectLink('showCampStat', {
            stat_type: 'campdate',
            ulogin: login
        }),
        'order-reports': UrlHelpers.getDirectLink('newPdfReport', {
            ulogin: login
        }),
        'phrases-positions-monitoring': UrlHelpers.getDirectLink('listWarnPlace', {
            ulogin: login,
            sort: 'addtime',
            reverse: '1',
            ws_place: '0',
            ws_time: '1d',
            ws_cid: '0',
            ws_done: '0',
            ws_client: login
        }),
        'retargeting-and-audiences': UrlHelpers.getRetargetingConditionUrl(login),
        feeds: UrlHelpers.getDirectLink('showFeeds', {
            ulogin: login,
            retpath: UrlHelpers.getRetPath()
        }),
        creatives: UrlHelpers.getDirectLink('showCreatives', {
            ulogin: login,
            retpath: UrlHelpers.getRetPath()
        }),
        'turbo-pages': UrlHelpers.getDirectLink('showTurboLandings', {
            ulogin: login,
            ClientID: ClientID
        }),
        'mobile-apps': UrlHelpers.createNewDirectLink(UrlHelpers.createNewDirectUrlPathByParams(APP_PREFIX, 'list'), {
            ulogin: login
        }),
        'agency-clients': UrlHelpers.getDirectLink('showClients'),
        'client-representatives': UrlHelpers.getDirectLink('showClReps', {
            ulogin: login
        }),
        'internationalization-interface': UrlHelpers.getDirectLink('i18nTmplList'),
        'upload-documents': UrlHelpers.getDirectLink('showUploadModerationDocs', {
            ulogin: login
        }),
        'statistic-by-all-old-campaigns': UrlHelpers.getDirectLink('showCampStat', {
            currency_archive: '1',
            stat_type: 'campdate',
            ulogin: login
        }),
        'minus-keywords-library': UrlHelpers.getMinusPhrasesLibraryUrl(login),
        'price-packages': UrlHelpers.getPricePackagesGridUrl(login)
    };

    return sidebarActions;
};
